// src/pages/admin/analytics/components/FinancialOverviewTab.jsx
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Package,
  AlertOctagon,
  ShieldCheck,
  RefreshCw,
  BarChart2,
  ShoppingBag,
  Truck,
  Info,
  ArrowUpRight,
  FileCheck,
} from 'lucide-react';

const FinancialOverviewTab = ({ data, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500 mr-3" />
        <span className="font-medium text-lg">Loading Financial Analytics...</span>
      </div>
    );
  }

  const summary = data?.summary || {};
  const metrics = data?.metrics || {};
  const percentages = data?.percentages || {};
  const recentTrends = data?.recentTrends || [];
  const salesBreakdown = data?.salesBreakdown || {};

  const totalSales = metrics.totalSales ?? summary.totalSales ?? 0;
  const cogs = metrics.cogs ?? summary.cogs ?? 0;
  const waste = metrics.waste ?? summary.waste ?? 0;
  const refunds = metrics.refunds ?? summary.refunds ?? 0;
  const transportClaims = metrics.transportClaims ?? summary.transportClaims ?? 0;
  const grnPurchases = metrics.grnPurchases ?? summary.grnPurchases ?? 0;
  const grnCount = metrics.grnCount ?? summary.grnCount ?? 0;
  const netProfit = summary.netProfit ?? metrics.profit ?? (totalSales - (cogs + waste + refunds + transportClaims));
  const profitMargin = summary.profitMargin ?? percentages.profitPercentage ?? (totalSales > 0 ? (netProfit / totalSales) * 100 : 0);

  return (
    <div className="space-y-6">
      {/* Top KPI Cards Grid - 5 Columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total Sales Revenue */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-4 shadow-md relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-bold text-blue-100 uppercase tracking-wider">Total Sales Revenue</p>
              <h3 className="text-xl font-black mt-1">Rs. {totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-lg">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-[11px] text-blue-100 mt-3 flex items-center">
            <TrendingUp className="h-3 w-3 mr-1" /> Gross income before COGS
          </p>
        </div>

        {/* GRN Buying Cost (COGS) */}
        <div className="bg-gradient-to-br from-slate-800 to-gray-900 text-white rounded-xl p-4 shadow-md relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">GRN Buying Cost (COGS)</p>
              <h3 className="text-xl font-black mt-1">Rs. {cogs.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-lg">
              <Package className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-[11px] text-slate-300 mt-3">
            {percentages.cogsPercentage || (totalSales > 0 ? ((cogs / totalSales) * 100).toFixed(1) : 0)}% of total sales
          </p>
        </div>

        {/* NEW: GRN Total Purchasing Inflow */}
        <div className="bg-gradient-to-br from-amber-600 to-orange-700 text-white rounded-xl p-4 shadow-md relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-bold text-amber-100 uppercase tracking-wider">GRN Total Purchasing</p>
              <h3 className="text-xl font-black mt-1">Rs. {grnPurchases.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-lg">
              <ShoppingBag className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-[11px] text-amber-100 mt-3 flex items-center">
            <FileCheck className="h-3 w-3 mr-1" /> {grnCount} Goods Receipt Notes
          </p>
        </div>

        {/* Wastage & Losses */}
        <div className="bg-gradient-to-br from-rose-600 to-red-700 text-white rounded-xl p-4 shadow-md relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-bold text-rose-100 uppercase tracking-wider">Wastage & Preparation Loss</p>
              <h3 className="text-xl font-black mt-1">Rs. {waste.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-lg">
              <AlertOctagon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-[11px] text-rose-100 mt-3">
            Prep waste & expired losses
          </p>
        </div>

        {/* Actual Net Profit */}
        <div className={`bg-gradient-to-br ${netProfit >= 0 ? 'from-emerald-600 to-teal-700' : 'from-red-700 to-rose-900'} text-white rounded-xl p-4 shadow-md relative overflow-hidden flex flex-col justify-between`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-bold text-emerald-100 uppercase tracking-wider">Actual Net Profit</p>
              <h3 className="text-xl font-black mt-1">Rs. {netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-lg">
              {netProfit >= 0 ? <TrendingUp className="h-5 w-5 text-white" /> : <TrendingDown className="h-5 w-5 text-white" />}
            </div>
          </div>
          <p className="text-[11px] text-emerald-100 mt-3 flex items-center font-bold">
            Net Margin: {profitMargin.toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Expense Deductions & GRN Breakdown Summary */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <BarChart2 className="h-5 w-5 text-orange-500 mr-2" />
          Financial Breakdown & Deductions Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="p-4 bg-blue-50/60 rounded-xl border border-blue-100">
            <p className="text-xs font-bold text-blue-700 uppercase">Gross Sales Revenue</p>
            <p className="text-lg font-black text-gray-900 mt-1">Rs. {totalSales.toFixed(2)}</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
            <p className="text-xs font-bold text-slate-600 uppercase">GRN Buying Cost (COGS)</p>
            <p className="text-lg font-black text-slate-800 mt-1">- Rs. {cogs.toFixed(2)}</p>
          </div>

          <div className="p-4 bg-amber-50/60 rounded-xl border border-amber-200">
            <p className="text-xs font-bold text-amber-800 uppercase">GRN Stock Purchasing</p>
            <p className="text-lg font-black text-amber-900 mt-1">+ Rs. {grnPurchases.toFixed(2)}</p>
            <p className="text-[10px] text-amber-700 font-semibold mt-0.5">{grnCount} GRNs Received</p>
          </div>

          <div className="p-4 bg-rose-50/60 rounded-xl border border-rose-100">
            <p className="text-xs font-bold text-rose-700 uppercase">Wastage & Expirations</p>
            <p className="text-lg font-black text-rose-600 mt-1">- Rs. {waste.toFixed(2)}</p>
          </div>

          <div className="p-4 bg-orange-50/60 rounded-xl border border-orange-100">
            <p className="text-xs font-bold text-orange-700 uppercase">Refunds & Claims</p>
            <p className="text-lg font-black text-orange-600 mt-1">- Rs. {(refunds + transportClaims).toFixed(2)}</p>
          </div>
        </div>

        {/* Visual Calculation Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-xs font-semibold text-gray-600 mb-1">
            <span>Gross Sales (100%)</span>
            <span>Net Profit Margin: {profitMargin.toFixed(1)}%</span>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden flex">
            <div
              className="bg-slate-700 h-full"
              style={{ width: `${Math.min(100, Math.max(0, percentages.cogsPercentage || 40))}%` }}
              title={`COGS: ${percentages.cogsPercentage}%`}
            ></div>
            <div
              className="bg-rose-500 h-full"
              style={{ width: `${Math.min(100, Math.max(0, percentages.wastePercentage || 5))}%` }}
              title={`Wastage: ${percentages.wastePercentage}%`}
            ></div>
            <div
              className="bg-emerald-500 h-full flex-1"
              title={`Net Profit: ${profitMargin}%`}
            ></div>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
            <span className="flex items-center"><span className="w-3 h-3 bg-slate-700 rounded-full mr-1.5"></span> COGS</span>
            <span className="flex items-center"><span className="w-3 h-3 bg-rose-500 rounded-full mr-1.5"></span> Wastage</span>
            <span className="flex items-center"><span className="w-3 h-3 bg-emerald-500 rounded-full mr-1.5"></span> Net Profit</span>
          </div>
        </div>
      </div>

      {/* Dedicated GRN Stock Purchasing & Supplier Inflow Analytics Card */}
      <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 rounded-2xl border border-amber-200/80 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start space-x-3">
            <div className="p-3 bg-amber-500 text-white rounded-xl shadow-md">
              <ShoppingBag className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-extrabold text-gray-900">
                  GRN Purchasing & Stock Inflow Suite
                </h3>
                <span className="px-2.5 py-0.5 bg-amber-200 text-amber-900 rounded-full text-[10px] font-black uppercase tracking-wider">
                  GRN Data Synced
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Real-time tracking of raw materials and inventory received from Goods Receipt Notes (GRN).
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-6 bg-white/80 backdrop-blur p-4 rounded-xl border border-amber-200 shadow-xs">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase">Total GRN Value</p>
              <p className="text-lg font-black text-amber-700">Rs. {grnPurchases.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="h-8 w-px bg-amber-200"></div>
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase">GRNs Received</p>
              <p className="text-lg font-black text-gray-900">{grnCount} Notes</p>
            </div>
            <div className="h-8 w-px bg-amber-200"></div>
            <div>
              <p className="text-[11px] font-semibold text-gray-500 uppercase">Purchases vs Sales COGS</p>
              <p className="text-lg font-black text-slate-800">
                {grnPurchases > 0 && cogs > 0 ? `${((cogs / grnPurchases) * 100).toFixed(1)}% COGS Sold` : 'Synced'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialOverviewTab;
